import React, { useMemo, Fragment, ReactElement, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import FavoriteOfferButton from '../../../atoms/buttons/FavoriteOfferButton';
import { getBackgroundTheme } from '../../../style';
import { renderAvatar, toaster } from '../../../common';
import FavOfferCreate from '../Offers/FavOfferCreate';
import OfferSuccess from '../Offers/OfferSuccess';
import OfferAccept from '../Offers/OfferAccept';
import OfferAcceptMessage from '../Offers/OfferAcceptMessage';
import OfferEdit from './OfferEdit';
import useModal from '../../../hooks/useModal';
import { useOffers } from '../../../contexts/OffersContext';
import { OFFERS_NEW_PATH } from '../../../constants/paths';
import Tooltip from '../../common/Tooltip';
import { declineOffer, counterModalClose } from '../../../actions/profileTrackAction';
import { cancelOffer, counterOffer as vysionCounterOffer } from '../../../actions/offerTrackAction';
import CreateNewOffer from './CreateNewOffer';
import { refreshProfile } from '../../../actions/profileActions';
import {
  createNewOffer,
  refreshOffers,
  counterOffer,
  sendSuggestion,
  ignoreOffer,
  ignoreOfferFromList,
  acceptOffer,
  showAcceptUnlockModalAction,
} from '../../../actions/offerActions';
import { AccountMaker } from '../../../models/Account';
import { mixpanelTrackEvent } from '../../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import OfferAmountButtonV2 from '../../../atoms/buttons/OfferAmountButtonV2';
import breakpoints from '../../../theme/material/breakpoints';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import { CustomStyledProps } from 'style/style';
import { useCommonModal } from 'contexts/ModalContext';
import { clearErrors } from '../../../actions/commonActions';

const OfferActionWrap = styled('div')(
  {
    margin: 0,
    justifyContent: 'middle',
    marginRight: 20,
    width: '100%',
    [breakpoints.down(639.05)]: {
      marginRight: 0,
    },
  },
  ({ viewType }: CustomStyledProps) =>
    viewType === 'grid'
      ? {
          backgroundColor: getBackgroundTheme().personalInfo,
          marginRight: 0,
          marginTop: 10,
        }
      : {
          width: 'unset',
          [breakpoints.down(639.05)]: {
            marginRight: 10,
          },
        }
);

const OfferActionsButtonWrap = styled('div')<{ isChangeView: boolean }>(
  {
    display: 'flex',
    justifyContent: 'space-around',
    flexFlow: 'row',
    alignItems: 'center',
    [breakpoints.between(769, 821)]: {
      justifyContent: 'space-between',
    },
    [breakpoints.down(694)]: {
      justifyContent: 'space-evenly',
    },
    [breakpoints.down(376)]: {
      justifyContent: 'space-between',
    },
  },
  ({ isChangeView }) =>
    isChangeView && {
      flexDirection: 'row-reverse',
    }
);

interface OfferActionButtonProps {
  offerKey: any;
  offer: any;
  viewType: any;
  otherAccount: any;
  isMatched: any;
  isSuggestRequest: any;
  pageTitle: string;
}

const handleVysionCounterOffer = () => vysionCounterOffer();

const OfferActionButton = ({
  offerKey,
  offer,
  viewType,
  otherAccount,
  isMatched,
  isSuggestRequest,
  pageTitle,
}: OfferActionButtonProps): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [showOfferEditModal, setShowOfferEditModal] = useState(false);
  const [showCreateNewOfferModal, setShowCreateNewOfferModal] = useState(false);
  const [showFavOfferCreateModal, setShowFavOfferCreateModal] = useState(false);
  const [showOfferAcceptModal, setShowOfferAcceptModal] = useState({
    isOpen: false,
    isFromCounter: false,
    username: '',
    hashId: '',
    forceTrigger: Date.now(),
    threadUid: '',
  });
  const [showOfferSuccessModal, setShowOfferSuccessModal] = useState({
    isOpen: false,
    amount: '',
    isCounter: false,
    isRevised: false,
    isSuggest: false,
    username: '',
    urls: null,
  });

  const [showOfferAcceptMessageModal, setShowOfferAcceptMessageModal] = useState({
    isOpen: false,
    amount: '',
    hashId: '',
    username: '',
    urls: null,
  });

  const user = useSelector((state: RootState) => state.profile);

  const trackMixPanelPageName = pageTitle === 'Received Requests' ? 'Received Offers' : pageTitle;
  const hasOffer = offer !== undefined && offer.data !== undefined;
  const offerData = offer && offer.data;
  const offerIsFromOtherUser =
    hasOffer && offerData.status === 'pending' && offerData.to_account_hash_id === user.hash_id;
  const otherProfile = otherAccount.profile.data;
  const isGenerous = otherProfile.account_type === 'Attractive';
  const canSendOffer = isMatched && !offer;
  const { setVisitedTabHandler } = useOffers();
  const accountModel = AccountMaker.create(user);

  const { setShowTooManyPendingOfferModal, setModalArgs } = useCommonModal();
  const { showMandatoryWall } = useMandatoryWall();

  const { confirmModal, resetModal } = useModal();
  const location = useLocation();

  const activePage = useMemo(() => location.pathname, [location]);
  const isReceivedOffersListViewDesktop = useMemo(
    () => !isMobile && viewType === 'list' && activePage === OFFERS_NEW_PATH,
    [isMobile, viewType, activePage]
  );

  const userCanMakeOffer = useMemo(() => user.can_make_offer, [user.can_make_offer]);

  const formatPrice = (currency, price) => {
    if (!currency || !price) {
      return '';
    }
    return `${currency.initial}${String.fromCharCode(currency.label)}${price}`;
  };

  const handleAttractiveMessage = (amount = '') => {
    showMandatoryWall(
      () => {
        setShowOfferAcceptModal({
          isOpen: false,
          isFromCounter: false,
          username: '',
          hashId: '',
          forceTrigger: showOfferAcceptModal.forceTrigger,
          threadUid: '',
        });

        setShowOfferAcceptMessageModal({
          isOpen: true,
          amount,
          hashId: otherAccount.hash_id,
          username: otherAccount.username,
          urls: otherProfile.avatar.data.urls,
        });
      },
      () => undefined,
      'Accept Offer'
    );
    resetModal();
  };

  const handleForceIgnoreOffer = async () => {
    await dispatch(ignoreOffer(otherAccount.hash_id, false, false, null, null));

    declineOffer();

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.OFFER_DECLINED,
      payload: {
        eventProperties: {
          Amount: offer.data.price_original,
          User: user.hash_id,
          Source: trackMixPanelPageName,
        },
      },
      otherAccountHashId: offer.data.initiator_hash_id,
    });

    toaster.warn('Offer Declined');

    resetModal();
  };

  const handleIgnoreOffer = () => {
    confirmModal({
      dataTestID: 'offer-cancel-sent-offers',
      modalContentLabel: 'Received Ignore',
      confirmationProps: {
        title: `Decline ${isGenerous ? 'Offer' : 'Request'}?`,
        message: `Are you sure you want to decline the ${
          isGenerous ? 'offer' : 'request'
        } from <strong>${otherAccount.username}</strong>?`,
        confirmText: `Decline ${isGenerous ? 'Offer' : 'Request'}`,
        confirmAction: async () => {
          await dispatch(ignoreOffer(otherAccount.hash_id, false, false, null, null));

          declineOffer();

          mixpanelTrackEvent({
            eventName: EVENT_NAMES.OFFER_DECLINED,
            payload: {
              eventProperties: {
                Amount: offer.data.price_original,
                User: user.hash_id,
                Source: trackMixPanelPageName,
              },
            },
            otherAccountHashId: offer.data.initiator_hash_id,
          });

          toaster.warn('Offer Declined');
          resetModal();
        },
      },
    });
  };

  const handleCancelSentOffer = () => {
    confirmModal({
      dataTestID: 'offer-cancel-sent-offers',
      modalContentLabel: 'Offers Cancel Sent Offers',
      confirmationProps: {
        title: `Cancel ${isGenerous ? 'Offer' : 'Request'}?`,
        message: `Are you sure you want to cancel your ${
          isGenerous ? 'offer' : 'request'
        } to <strong>${otherAccount.username}</strong>?`,
        confirmText: `Cancel ${isGenerous ? 'Offer' : 'Request'}`,
        confirmAction: async () => {
          await dispatch(ignoreOfferFromList(offer.data, location.pathname, params.offerType, navigate));

          cancelOffer();

          mixpanelTrackEvent({
            eventName: EVENT_NAMES.OFFER_CANCELLED,
            payload: {
              eventProperties: {
                Amount: offer.data.price_original,
                User: user.hash_id,
                Source: 'sent offers',
              },
            },
            otherAccountHashId: offer.data.initiator_hash_id,
          });

          resetModal();
        },
      },
    });
  };

  const handleOfferSuccess = (
    toggle: boolean,
    amount = '',
    isCounter = false,
    isRevised = false,
    isSuggest = false
  ) => {
    if (isCounter) {
      setVisitedTabHandler('sent', false);
    }

    showMandatoryWall(
      () => {
        setShowOfferEditModal(false);
        setShowCreateNewOfferModal(false);
        setShowOfferSuccessModal({
          isOpen: toggle,
          amount: amount,
          isCounter,
          isRevised,
          isSuggest,
          username: otherAccount.username,
          urls: otherProfile.avatar.data.urls,
        });
      },
      () => undefined,
      'Accept Offer'
    );
  };

  const handleAcceptUnlock = (amount, creditCost, threadUid, hashId, name = '', isFromConfirmModal = false) => {
    isFromConfirmModal && resetModal();
    setShowOfferAcceptModal({
      isOpen: false,
      isFromCounter: false,
      username: '',
      hashId: otherAccount.hash_id,
      forceTrigger: Date.now(),
      threadUid,
    });
    setTimeout(() => {
      dispatch(
        showAcceptUnlockModalAction({
          isOpen: true,
          offerAmount: amount,
          creditCost: creditCost,
          hashId: hashId,
          username: name,
          urls: otherProfile.avatar.data.urls,
          forceTrigger: Date.now(),
          isGenerous: isGenerous,
          threadUid,
        })
      );
    }, 100);
  };

  const handleCreateNewOffer = (formData, shouldRefreshList = false) => {
    return dispatch(createNewOffer(formData, shouldRefreshList, null, null, true));
  };

  const handleCounterOffer = formData => {
    handleVysionCounterOffer();

    return dispatch(counterOffer(formData, false, navigate, offerKey, null, true));
  };

  const handleSuggestion = (profileId, showToastMessage = true) => {
    return dispatch(sendSuggestion(profileId, true, offerKey, null, showToastMessage));
  };

  const handleAcceptOffer = (username: string, message: string, source: string) => {
    return dispatch(acceptOffer(username, message, false, null, null, true, source, navigate));
  };

  const handleNewOffer = () => {
    showMandatoryWall(
      () => {
        setShowOfferEditModal(false);
        setShowCreateNewOfferModal(true);
      },
      () => undefined,
      'Edit Sent Offer'
    );
  };

  const handleEditSentOffer = () => {
    showMandatoryWall(
      () => {
        if (!userCanMakeOffer && isGenerous && accountModel.shouldShowTooManyLockedOffersDeals()) {
          setShowTooManyPendingOfferModal(true);
          return false;
        }

        setShowOfferEditModal(true);
      },
      () => undefined,
      'Edit Sent Offer'
    );
  };

  const handleForceOpenCounterModal = () => {
    showMandatoryWall(
      () => {
        if (!userCanMakeOffer && isGenerous) {
          if (accountModel.shouldShowTooManyLockedOffersDeals()) {
            setShowTooManyPendingOfferModal(true);
            return false;
          }

          if (offerIsFromOtherUser && accountModel.shouldShowTooManySentOffers()) {
            setShowTooManyPendingOfferModal(true);
            setModalArgs({
              type: 'sent',
            });
            return false;
          }
        }

        setShowOfferAcceptModal({
          isOpen: true,
          isFromCounter: true,
          username: otherAccount.username,
          hashId: otherAccount.hash_id,
          forceTrigger: Date.now(),
          threadUid: '',
        });
      },
      () => undefined,
      'Counter Offer'
    );
  };

  const renderAcceptButton = () => {
    if (!offerIsFromOtherUser) {
      return '';
    }

    if (isSuggestRequest) {
      return renderSendOfferButton();
    }

    return (
      <FavoriteOfferButton
        data-test-id="offer-btn-accept"
        hasOffer={hasOffer}
        offer={offer}
        viewType={viewType}
        type="accept"
        isFromOffer={true}
        onClick={() => {
          showMandatoryWall(
            () => {
              setShowOfferAcceptModal({
                isOpen: true,
                isFromCounter: false,
                username: otherAccount.username,
                hashId: otherAccount.hash_id,
                forceTrigger: Date.now(),
                threadUid: '',
              });
            },
            () => undefined,
            'Accept Offer'
          );
        }}
      />
    );
  };

  const renderSendOfferButton = () => {
    return (
      <FavoriteOfferButton
        data-test-id="offer-btn-offer"
        hasOffer={hasOffer}
        offer={offer}
        viewType={viewType}
        type="offer"
        forceLabel={null}
        isFromOffer={isReceivedOffersListViewDesktop ? false : true}
        onClick={() => {
          showMandatoryWall(
            () => {
              if (!userCanMakeOffer && isGenerous && accountModel.shouldShowTooManyLockedOffersDeals()) {
                setShowTooManyPendingOfferModal(true);
                return false;
              }

              setShowFavOfferCreateModal(true);
            },
            () => undefined,
            'Make Offer'
          );
        }}
      />
    );
  };

  const renderDesktopListViewButtons = () => {
    return (
      <div style={{ display: 'flex' }}>
        <FavoriteOfferButton
          data-test-id="offer-btn-offer"
          hasOffer={hasOffer}
          offer={offer}
          type="decline"
          onClick={!isReceivedOffersListViewDesktop ? handleForceIgnoreOffer : handleIgnoreOffer}
        />
        {isSuggestRequest ? (
          renderSendOfferButton()
        ) : (
          <Fragment>
            <FavoriteOfferButton
              data-test-id="offer-btn-offer"
              hasOffer={hasOffer}
              offer={offer}
              type="counter"
              onClick={handleForceOpenCounterModal}
            />
            <FavoriteOfferButton
              data-test-id="offer-btn-offer"
              hasOffer={hasOffer}
              offer={offer}
              type="accept"
              label="Accept"
              onClick={() => {
                showMandatoryWall(
                  () => {
                    if (!userCanMakeOffer && isGenerous && accountModel.shouldShowTooManyLockedOffersDeals('accept')) {
                      setShowTooManyPendingOfferModal(true);
                      return false;
                    }

                    confirmModal({
                      includeFooter: true,
                      includeAwarenessText: true,
                      dataTestID: 'offer-accept-offers',
                      modalContentLabel: 'Accept Offer',
                      confirmationProps: {
                        title: `Accept ${isGenerous ? 'Offer' : 'Request'}?`,
                        message: `Are you sure you want to accept the ${
                          isGenerous ? 'offer' : 'request'
                        } from <strong>${otherAccount.username}</strong>?`,
                        confirmText: `Accept ${isGenerous ? 'Offer' : 'Request'}`,
                        confirmAction: async () => {
                          if (isGenerous) {
                            const result = await handleAcceptOffer(otherAccount.hash_id, '', pageTitle);

                            handleAcceptUnlock(
                              offer.data.price_original,
                              result.credit_cost,
                              result.threadUid,
                              result.to_account_hash_id,
                              otherAccount.username,
                              true
                            );
                          } else {
                            handleAttractiveMessage(offer.data.price_original);
                          }
                        },
                      },
                    });
                  },
                  () => undefined,
                  'Accept Offer'
                );
              }}
            />
          </Fragment>
        )}
      </div>
    );
  };

  return (
    <OfferActionWrap viewType={viewType}>
      <OfferActionsButtonWrap isChangeView={isReceivedOffersListViewDesktop}>
        {activePage === '/offers/sent' ? (
          <Fragment>
            <FavoriteOfferButton
              data-test-id="offer-btn-offer"
              hasOffer={hasOffer}
              offer={offer}
              viewType={viewType}
              type="cancel"
              isFromOffer={viewType === 'grid' ? true : false}
              onClick={handleCancelSentOffer}
            />
            <Tooltip
              variant="primary"
              htmlElementLabel={`<strong>Edit/Revise</strong> your Offer here`}
              trigger="hover"
              placement="top"
            >
              <OfferAmountButtonV2
                type="sent"
                viewType={viewType}
                price={formatPrice(
                  {
                    initial: offer.data.currency_original.data.initial,
                    label: offer.data.currency_original.data.label_unicode,
                  },
                  offer.data.price_original
                )}
                hasHoverEffect={true}
                onClick={handleEditSentOffer}
              />
            </Tooltip>
            <OfferEdit
              isModalOpen={showOfferEditModal}
              avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
              username={otherAccount.username}
              hashId={otherAccount.hash_id}
              closeModal={() => setShowOfferEditModal(false)}
              currencyInitial={otherAccount.currency.data.initial}
              currencyLabel={otherAccount.currency.data.label_unicode}
              currentUserIsGenerous={isGenerous}
              buttonID="editOffer"
              currentOffer={offer.data.price_original}
              successOpen={handleOfferSuccess}
              sex={user && user.profile.data.sex === 'Male' ? 'Female' : 'Male'}
              onCancelAndSendNewOfferHandler={async () => {
                await dispatch(ignoreOfferFromList(offer.data, location.pathname, params.offerType, navigate));

                cancelOffer();

                mixpanelTrackEvent({
                  eventName: EVENT_NAMES.OFFER_CANCELLED,
                  payload: {
                    eventProperties: {
                      Amount: offer.data.price_original,
                      User: user.hash_id,
                      Source: 'sent offers',
                    },
                  },
                  otherAccountHashId: offer.data.initiator_hash_id,
                });

                handleNewOffer();
              }}
            />
            <CreateNewOffer
              isModalOpen={showCreateNewOfferModal}
              avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
              username={otherAccount.username}
              hashId={otherAccount.hash_id}
              createNewOffer={handleCreateNewOffer}
              onOfferSuccess={() => {
                dispatch(refreshProfile());
                dispatch(refreshOffers('sent', 'recent'));
              }}
              successOpen={handleOfferSuccess}
              closeModal={() => setShowCreateNewOfferModal(false)}
              currencyInitial={otherAccount.currency.data.initial}
              currencyLabel={otherAccount.currency.data.label_unicode}
              currentUserIsGenerous={isGenerous}
              sex={otherProfile.sex}
              sendSuggestion={hashId => handleSuggestion(hashId, false)}
              cannot_make_offer_reasons={user.cannot_make_offer_reasons}
              buttonID="interestedOffers"
              isSuggestRequest={false}
              title={isGenerous ? 'Make an Offer!' : 'Request A Date!'}
              pageTitle={'Make Request'}
              defaultOfferAmount={otherProfile?.default_offer_amount}
            />
          </Fragment>
        ) : (
          <Fragment>
            <OfferAmountButtonV2
              type="received"
              viewType={viewType}
              price={formatPrice(
                {
                  initial: offer.data.currency_original.data.initial,
                  label: offer.data.currency_original.data.label_unicode,
                },
                offer.data.price_original
              )}
              hasHoverEffect={true}
              onClick={handleForceOpenCounterModal}
            />
            {canSendOffer && renderSendOfferButton()}
            {isReceivedOffersListViewDesktop ? renderDesktopListViewButtons() : renderAcceptButton()}
          </Fragment>
        )}

        <OfferSuccess
          isModalOpen={showOfferSuccessModal.isOpen}
          onClose={() => {
            setShowOfferSuccessModal({
              isOpen: false,
              amount: '',
              isCounter: false,
              isRevised: false,
              isSuggest: false,
              username: '',
              urls: null,
            });
          }}
          offerAmount={showOfferSuccessModal.amount}
          avatarSrc={showOfferSuccessModal?.urls && renderAvatar(showOfferSuccessModal.urls, true)}
          username={showOfferSuccessModal.username}
          buttonID="interestedOffers"
          isCounter={showOfferSuccessModal.isCounter}
          isRevised={showOfferSuccessModal.isRevised}
          isSuggest={showOfferSuccessModal.isSuggest}
        />

        <OfferAccept
          key={offerKey}
          isModalOpen={showOfferAcceptModal.isOpen}
          acceptUnlock={handleAcceptUnlock}
          avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
          username={showOfferAcceptModal.username}
          hashId={showOfferAcceptModal.hashId}
          onHandleSubmit={() => handleAcceptOffer(showOfferAcceptModal.hashId, '', pageTitle)}
          onHandleAttractiveSubmit={handleAttractiveMessage}
          successOpen={handleOfferSuccess}
          counterOffer={handleCounterOffer}
          closeModal={() => {
            setShowOfferAcceptModal({
              isOpen: false,
              isFromCounter: false,
              username: '',
              hashId: '',
              forceTrigger: Date.now(),
              threadUid: '',
            });
            resetModal();
            if (showOfferAcceptModal.isFromCounter) {
              counterModalClose();
            }
          }}
          currentUserIsGenerous={isGenerous}
          buttonID="acceptOffer"
          offer={offer}
          ignoreOffer={!isReceivedOffersListViewDesktop ? handleForceIgnoreOffer : handleIgnoreOffer}
          forceCounter={showOfferAcceptModal.isFromCounter}
          forceTrigger={showOfferAcceptModal.forceTrigger}
          pageTitle={pageTitle}
        />

        <OfferAcceptMessage
          isModalOpen={showOfferAcceptMessageModal.isOpen}
          offerAmount={showOfferAcceptMessageModal.amount}
          avatarSrc={showOfferAcceptMessageModal?.urls && renderAvatar(showOfferAcceptMessageModal.urls, true)}
          username={showOfferAcceptMessageModal.username}
          buttonID="interestedOffers"
          closeModal={() => {
            setShowOfferAcceptMessageModal({ isOpen: false, amount: '', hashId: '', username: '', urls: null });
            dispatch(clearErrors());
          }}
          onSubmit={dealMessage => {
            return handleAcceptOffer(showOfferAcceptMessageModal.hashId, dealMessage, pageTitle);
          }}
        />

        <FavOfferCreate
          isModalOpen={showFavOfferCreateModal}
          avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
          username={otherAccount.username}
          hashId={otherAccount.hash_id}
          createNewOffer={handleCreateNewOffer}
          successOpen={handleOfferSuccess}
          counterOffer={handleCounterOffer}
          isCounter={false}
          currencyInitial={otherAccount.currency.data.initial}
          currencyLabel={otherAccount.currency.data.label_unicode}
          currentUserIsGenerous={isGenerous}
          sex={otherProfile.sex}
          sendSuggestion={handleSuggestion}
          cannot_make_offer_reasons={user.cannot_make_offer_reasons}
          buttonID="interestedOffers"
          isSuggestRequest={isSuggestRequest}
          title="Make an Offer!"
          pageTitle={pageTitle}
          otherProfile={otherProfile}
          closeModal={() => {
            setShowFavOfferCreateModal(false);
          }}
        />
      </OfferActionsButtonWrap>
    </OfferActionWrap>
  );
};

export default OfferActionButton;
